import { useState, useCallback, useMemo } from 'react';
import useDataQuery from '@hooks/use-data-query';
import { ProcessStatusEnum } from '@api/enum';
import { getLiveLapseLogs } from '@api/services/analytics.service';

const useLiveLapseLogsListData = (startDate: string, endDate: string, initialFilter: string = 'ALL') => {
    const [filterValue, setFilterValue] = useState(initialFilter);

    const changeFilterValue = useCallback((val: string | number) => {
        setFilterValue(val as ProcessStatusEnum | 'ALL');
    }, []);

    const logsQuery = useDataQuery(['live-lapse-logs', startDate, endDate], () => getLiveLapseLogs(startDate, endDate));

    const allLogs = logsQuery.data || [];

    const filteredLogs = useMemo(() => {
        if (logsQuery.isLoading) return [];
        return filterValue === 'ALL'
            ? allLogs
            : allLogs.filter((log: { status: string }) => log.status === filterValue);
    }, [logsQuery.isLoading, filterValue, allLogs]);

    const statusCounts = useMemo(() => {
        const counts: Record<ProcessStatusEnum, number> = {
            [ProcessStatusEnum.PENDING]: 0,
            [ProcessStatusEnum.COMPLETED]: 0,
            [ProcessStatusEnum.FAILED]: 0,
            [ProcessStatusEnum.EXPIRED]: 0,
        };

        for (const log of allLogs) {
            const status = log.status as ProcessStatusEnum;
            if (status in counts) {
                counts[status]++;
            }
        }

        return {
            ...counts,
            ALL: allLogs.length,
        };
    }, [allLogs]);

    return {
        data: filteredLogs,
        isLoading: logsQuery.isLoading,
        filterValue,
        changeFilterValue,
        statusCounts,
    };
};

export default useLiveLapseLogsListData;
